<template>
  <div
    v-if="loading"
    class="flex justify-center items-center py-8">
    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
  </div>

  <div
    v-else-if="!data || !data.overview"
    class="text-center py-8">
    <div class="bg-amber-50 border-l-4 border-amber-400 p-4 mb-4">
      <p class="text-amber-700">
        No checkin data found for this organization.
        Checkins will appear here once moments are posted.
      </p>
    </div>
  </div>

  <div
    v-else
    class="space-y-6">
    <!-- Overview Stats -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600">
              Total Checkins
            </p>
            <p class="text-2xl font-bold text-gray-900">
              {{ getProp(data.overview, 'totalMoments', 'total_moments') || 0 }}
            </p>
          </div>
          <div class="text-indigo-600 bg-indigo-50 rounded-lg p-2">
            <i class="fas fa-map-marker-alt text-xl" />
          </div>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600">
              Last 30 Days
            </p>
            <p class="text-2xl font-bold text-gray-900">
              {{ getProp(data.overview, 'last30Days', 'last_30_days') || 0 }}
            </p>
            <p
              v-if="growthRate !== 0"
              class="text-xs mt-1"
              :class="growthRateClass">
              <i
                class="fas"
                :class="growthRateIcon" />
              {{ Math.abs(growthRate) }}%
            </p>
            <p
              v-else
              class="text-xs mt-1 text-gray-500">
              No change
            </p>
          </div>
          <div class="text-emerald-600 bg-emerald-50 rounded-lg p-2">
            <i class="fas fa-chart-line text-xl" />
          </div>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600">
              With Images
            </p>
            <p class="text-2xl font-bold text-gray-900">
              {{ getProp(data.overview, 'withImages', 'with_images') || 0 }}
            </p>
            <p class="text-xs text-gray-500 mt-1">
              {{ imagePercentage }}% of total
            </p>
          </div>
          <div class="text-violet-600 bg-violet-50 rounded-lg p-2">
            <i class="fas fa-camera text-xl" />
          </div>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600">
              Avg Per Day
            </p>
            <p class="text-2xl font-bold text-gray-900">
              {{ getProp(data.overview, 'averagePerDay', 'average_per_day') || 0 }}
            </p>
            <p class="text-xs text-gray-500 mt-1">
              Last 30 days
            </p>
          </div>
          <div class="text-amber-600 bg-amber-50 rounded-lg p-2">
            <i class="fas fa-calendar-day text-xl" />
          </div>
        </div>
      </div>
    </div>

    <!-- Timeline Chart -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <h4 class="text-md font-semibold mb-4 flex items-center">
        <i class="fas fa-chart-area text-blue-600 mr-2" />
        Checkin Activity Timeline
      </h4>
      <div
        class="relative"
        style="height: 250px;">
        <canvas ref="timelineChart" />
      </div>
    </div>

    <!-- Categories and Employees -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <!-- Top Categories -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <h4 class="text-md font-semibold mb-4 flex items-center">
          <i class="fas fa-tags text-violet-600 mr-2" />
          Top Categories
        </h4>
        <div v-if="data.categories && data.categories.length > 0">
          <div
            v-for="(category, index) in data.categories.slice(0, 5)"
            :key="index"
            class="mb-3">
            <div class="flex items-center justify-between mb-1">
              <span class="text-sm font-medium text-gray-700">
                {{ category.name }}
              </span>
              <span class="text-sm text-gray-600">
                {{ category.count }}
              </span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-2">
              <div
                class="h-2 rounded-full transition-all duration-500"
                :class="getCategoryBarColor(index)"
                :style="`width: ${getCategoryPercentage(category.count)}%`" />
            </div>
          </div>
        </div>
        <div
          v-else
          class="text-center py-4 text-gray-500 text-sm">
          No categories found
        </div>
      </div>

      <!-- Top Employees -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <h4 class="text-md font-semibold mb-4 flex items-center">
          <i class="fas fa-users text-emerald-600 mr-2" />
          Most Active Employees
        </h4>
        <div v-if="data.employees && data.employees.length > 0">
          <div
            v-for="(employee, index) in data.employees.slice(0, 5)"
            :key="index"
            class="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
            <div class="flex items-center">
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold mr-3"
                :class="getEmployeeAvatarColor(index)">
                {{ getInitials(employee.name) }}
              </div>
              <div>
                <div class="text-sm font-medium text-gray-900">
                  {{ employee.name }}
                </div>
                <div class="text-xs text-gray-500">
                  Last active: {{ formatLastActive(getProp(employee, 'lastActive', 'last_active')) }}
                </div>
              </div>
            </div>
            <div class="text-sm font-semibold text-gray-700">
              {{ employee.count }}
            </div>
          </div>
        </div>
        <div
          v-else
          class="text-center py-4 text-gray-500 text-sm">
          No employee data found
        </div>
      </div>
    </div>

    <!-- Location Breakdown -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <h4 class="text-md font-semibold mb-4 flex items-center">
        <i class="fas fa-map-marked-alt text-teal-600 mr-2" />
        Location Activity
      </h4>
      <div
        v-if="data.locations && data.locations.length > 0"
        class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead class="text-xs text-gray-600 bg-gray-50 border-b border-gray-200">
            <tr>
              <th class="px-4 py-3 text-left font-semibold">
                Location
              </th>
              <th class="px-4 py-3 text-center font-semibold">
                Checkins
              </th>
              <th class="px-4 py-3 text-center font-semibold">
                Activity
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr
              v-for="(location, index) in data.locations"
              :key="index"
              class="hover:bg-gray-50 transition-colors">
              <td class="px-4 py-3">
                <div>
                  <div class="font-medium text-gray-900">
                    {{ location.name }}
                  </div>
                  <div v-if="location.address" class="text-xs text-gray-500 mt-0.5">
                    {{ location.address }}
                  </div>
                  <div v-if="location.phone" class="text-xs text-gray-500">
                    <i class="fas fa-phone mr-1" />{{ location.phone }}
                  </div>
                </div>
              </td>
              <td class="px-4 py-3 text-center">
                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {{ location.count }}
                </span>
              </td>
              <td class="px-4 py-3">
                <div class="flex justify-center">
                  <Sparkline
                    v-if="locationSparklines[location.name]"
                    :data="locationSparklines[location.name]"
                    :width="60"
                    :height="20"
                    :color="'#3F83F8'"
                    :stroke-width="1.5"
                    :show-dot="false" />
                  <span
                    v-else
                    class="text-xs text-gray-400">—</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        v-else
        class="text-center py-4 text-gray-500 text-sm">
        No location data available
      </div>
    </div>

    <!-- Usage Patterns -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <!-- Day of Week Distribution -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <h4 class="text-md font-semibold mb-4 flex items-center">
          <i class="fas fa-calendar-week text-indigo-600 mr-2" />
          Activity by Day of Week
        </h4>
        <div
          class="relative"
          style="height: 200px;">
          <canvas ref="dayOfWeekChart" />
        </div>
      </div>

      <!-- Hour of Day Distribution -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <h4 class="text-md font-semibold mb-4 flex items-center">
          <i class="fas fa-clock text-amber-600 mr-2" />
          Activity by Hour
        </h4>
        <div
          class="relative"
          style="height: 200px;">
          <canvas ref="hourOfDayChart" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref, computed, onMounted, onBeforeUnmount, nextTick, watch
} from 'vue'
import axios from 'axios'
import { Chart, registerables } from 'chart.js'
import Sparkline from './components/sparkline.vue'

Chart.register(...registerables)

const props = defineProps({
  organizationId: {
    type: String,
    required: true
  }
})

// State
const loading = ref(true)
const data = ref(null)

// Chart refs
const timelineChart = ref(null)
const dayOfWeekChart = ref(null)
const hourOfDayChart = ref(null)
const chartInstances = ref({})

// Helper to access properties in both camelCase and snake_case
const getProp = (obj, camelCase, snakeCase) => {
  if (!obj) return null
  return obj[camelCase] !== undefined ? obj[camelCase] : obj[snakeCase]
}

// Computed
const growthRate = computed(() => {
  if (!data.value || !data.value.overview) return 0
  return getProp(data.value.overview, 'growthRate', 'growth_rate') || 0
})

const growthRateClass = computed(() => {
  const rate = growthRate.value
  if (rate > 0) return 'text-emerald-600'
  if (rate < 0) return 'text-red-600'
  return 'text-gray-600'
})

const growthRateIcon = computed(() => {
  const rate = growthRate.value
  if (rate > 0) return 'fa-arrow-up'
  if (rate < 0) return 'fa-arrow-down'
  return 'fa-minus'
})

const imagePercentage = computed(() => {
  if (!data.value || !data.value.overview) return 0
  const total = getProp(data.value.overview, 'totalMoments', 'total_moments') || 1
  const withImages = getProp(data.value.overview, 'withImages', 'with_images') || 0
  return Math.round((withImages / total) * 100)
})

const locationSparklines = computed(() => ({}))

// Methods
const getCategoryPercentage = (count) => {
  if (!data.value || !data.value.categories || data.value.categories.length === 0) return 0
  const maxCount = Math.max(...data.value.categories.map((c) => c.count))
  return Math.round((count / maxCount) * 100)
}

const getCategoryBarColor = (index) => {
  const colors = [
    'bg-violet-500',
    'bg-blue-500',
    'bg-emerald-500',
    'bg-amber-500',
    'bg-red-500'
  ]
  return colors[index % colors.length]
}

const getEmployeeAvatarColor = (index) => {
  const colors = [
    'bg-indigo-600',
    'bg-emerald-600',
    'bg-violet-600',
    'bg-amber-600',
    'bg-blue-600'
  ]
  return colors[index % colors.length]
}

const getInitials = (name) => {
  if (!name) return '?'
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

const formatLastActive = (date) => {
  if (!date) return 'Never'

  const d = new Date(date)
  const now = new Date()
  const diffInMs = now - d
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

  if (diffInDays === 0) return 'Today'
  if (diffInDays === 1) return 'Yesterday'
  if (diffInDays < 7) return `${diffInDays} days ago`
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`

  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// Chart methods
const destroyCharts = () => {
  Object.values(chartInstances.value).forEach((chart) => {
    if (chart) chart.destroy()
  })
  chartInstances.value = {}
}

const renderTimelineChart = () => {
  if (!timelineChart.value || !data.value?.timeline) return

  const ctx = timelineChart.value.getContext('2d')

  chartInstances.value.timeline = new Chart(ctx, {
    type: 'line',
    data: {
      labels: data.value.timeline.labels,
      datasets: [{
        label: 'Checkins',
        data: data.value.timeline.data,
        borderColor: '#3F83F8',
        backgroundColor: 'rgba(63, 131, 248, 0.1)',
        borderWidth: 2,
        tension: 0.3,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 4
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: 'rgba(17, 24, 39, 0.9)',
          titleColor: '#fff',
          bodyColor: '#fff',
          padding: 8,
          displayColors: false
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(156, 163, 175, 0.1)',
            drawBorder: false
          },
          ticks: {
            color: '#4B5563',
            font: {
              size: 11
            }
          }
        },
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: '#4B5563',
            font: {
              size: 11
            },
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 10
          }
        }
      }
    }
  })
}

const renderDayOfWeekChart = () => {
  if (!dayOfWeekChart.value || !data.value?.patterns) return

  const ctx = dayOfWeekChart.value.getContext('2d')
  const dayData = getProp(data.value.patterns, 'byDayOfWeek', 'by_day_of_week') || []

  chartInstances.value.dayOfWeek = new Chart(ctx, {
    type: 'bar',
    data: {
      labels: dayData.map((d) => d.day.slice(0, 3)),
      datasets: [{
        data: dayData.map((d) => d.count),
        backgroundColor: '#6875F5',
        borderRadius: 4
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: 'rgba(17, 24, 39, 0.9)',
          titleColor: '#fff',
          bodyColor: '#fff',
          padding: 8,
          callbacks: {
            label: (context) => `Checkins: ${context.parsed.y}`
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(156, 163, 175, 0.1)',
            drawBorder: false
          },
          ticks: {
            color: '#4B5563',
            font: {
              size: 10
            }
          }
        },
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: '#4B5563',
            font: {
              size: 10
            }
          }
        }
      }
    }
  })
}

const renderHourOfDayChart = () => {
  if (!hourOfDayChart.value || !data.value?.patterns) return

  const ctx = hourOfDayChart.value.getContext('2d')
  const hourData = getProp(data.value.patterns, 'byHour', 'by_hour') || []

  // Group hours into ranges for better visualization
  const hourRanges = [
    { label: '12AM-6AM', hours: [0, 1, 2, 3, 4, 5]},
    { label: '6AM-12PM', hours: [6, 7, 8, 9, 10, 11]},
    { label: '12PM-6PM', hours: [12, 13, 14, 15, 16, 17]},
    { label: '6PM-12AM', hours: [18, 19, 20, 21, 22, 23]}
  ]

  const rangeData = hourRanges.map((range) => {
    const count = range.hours.reduce((sum, hour) => {
      const hourItem = hourData.find((h) => h.hour === hour)
      return sum + (hourItem ? hourItem.count : 0)
    }, 0)
    return count
  })

  chartInstances.value.hourOfDay = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: hourRanges.map((r) => r.label),
      datasets: [{
        data: rangeData,
        backgroundColor: [
          '#E3A008',
          '#FACA15',
          '#F05252',
          '#7E3AF2'
        ],
        borderWidth: 0
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            padding: 15,
            font: {
              size: 11
            }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(17, 24, 39, 0.9)',
          titleColor: '#fff',
          bodyColor: '#fff',
          padding: 8
        }
      }
    }
  })
}

const renderCharts = () => {
  destroyCharts()

  nextTick(() => {
    renderTimelineChart()
    renderDayOfWeekChart()
    renderHourOfDayChart()
  })
}

const loadData = async() => {
  try {
    loading.value = true
    const response = await axios.get(`/ajax/organizations/${props.organizationId}/checkins_analytics`, {
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
      }
    })
    data.value = response.data
  } catch (error) {
    // Handle error silently
  } finally {
    loading.value = false
  }
}

// Watchers
watch(data, () => {
  if (data.value) {
    nextTick(() => {
      renderCharts()
    })
  }
}, { deep: true })

// Lifecycle
onMounted(() => {
  loadData()
})

onBeforeUnmount(() => {
  destroyCharts()
})
</script>

<style scoped>
/* Custom styles if needed */
</style>
